import React, { useState, useEffect } from 'react';
import { CreditCard, Download, Plus } from 'lucide-react';
import api from '../../../api';
import './AccountSections.css';

const BillingSection = ({ userId }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, [userId]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch payment methods
      const paymentMethodsResponse = await api.get(`/brands/${userId}/billing/payment-methods`);
      
      setPaymentMethods(paymentMethodsResponse.data || []);

      // Fetch invoices
      const invoicesResponse = await api.get(`/brands/${userId}/billing/invoices`);
      setInvoices(invoicesResponse.data || []);
    } catch (err) {
      let errorMessage = 'Failed to load billing data';
      if (err.response) {
        if (err.response.status === 403) {
          errorMessage = 'Access denied. Please log in again.';
        } else if (err.response.status === 404) {
          errorMessage = err.response.data?.message || 'Brand not found';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      }

      setError(errorMessage);
      setPaymentMethods([]);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentMethod = () => {
    alert('Add payment method functionality coming soon!');
  };

  const handleDownloadInvoice = (invoiceNumber) => {
    alert(`Downloading invoice ${invoiceNumber}`);
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2 className="section-title">Billing</h2>
        <p className="section-description">Manage your payment methods and invoices</p>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Loading billing data...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Payment Methods */}
          <div className="billing-section">
            <div className="section-header-row">
              <h3 className="section-subtitle">Payment Methods</h3>
              <button onClick={handleAddPaymentMethod} className="btn-outline">
                <Plus size={18} className="inline mr-2" />
                Add Payment Method
              </button>
            </div>

            {paymentMethods.length > 0 ? (
              <div className="payment-methods-grid">
                {paymentMethods.map((method) => (
                  <div key={method.id} className="payment-card">
                    <div className="payment-card-header">
                      <CreditCard size={32} className="text-blue-600" />
                      {method.isDefault && (
                        <span className="default-badge">Default</span>
                      )}
                    </div>
                    <div className="payment-card-body">
                      <p className="payment-type">{method.type}</p>
                      <p className="payment-number">•••• •••• •••• {method.last4}</p>
                      <p className="payment-expiry">Expires {method.expiryDate}</p>
                    </div>
                    <div className="payment-card-actions">
                      {!method.isDefault && (
                        <button className="btn-text">Set as Default</button>
                      )}
                      <button className="btn-text text-red-600">Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 p-4">No payment methods found</p>
            )}
          </div>

          {/* Invoices */}
          <div className="billing-section">
            <h3 className="section-subtitle">Billing History</h3>

            {invoices.length > 0 ? (
              <div className="table-container">
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Invoice Number</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((invoice) => (
                        <tr key={invoice.id}>
                          <td className="font-medium">{invoice.invoiceNumber}</td>
                          <td>{new Date(invoice.date).toLocaleDateString()}</td>
                          <td className="font-semibold">₹{Number(invoice.amount).toLocaleString('en-IN')}</td>
                          <td>
                            <span className={`status-badge ${invoice.status.toLowerCase()}`}>{invoice.status}</span>
                          </td>
                          <td>
                            <button
                              onClick={() => handleDownloadInvoice(invoice.invoiceNumber)}
                              className="btn-icon"
                              title="Download invoice"
                            >
                              <Download size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 p-4">No invoices found</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BillingSection;

